import type { LotteryName } from '../../lottery-purchases/types';
import type { Drawing, DrawingsResponse } from '../types';

type PickerDrawingOptions = {
  lotteryName?: LotteryName;
  nextDrawingDate?: number;
  nextClosingDate?: number;
  nextDrawingId?: string;
  /**
   * How long before `nextClosingDate` the picker includes the upcoming draw as a placeholder.
   */
  nextCloseLeadTime?: number;
  now?: number;
  maxDrawings?: number;
};

function sortDrawingsDescending(data: DrawingsResponse): Drawing[] {
  return [...data].sort((aa, bb) => new Date(bb.drawingDate).getTime() - new Date(aa.drawingDate).getTime());
}

function isUpcomingDrawing(drawing: Drawing, { nextDrawingId }: PickerDrawingOptions): boolean {
  return drawing.drawingId === nextDrawingId;
}

function hasPublishedDrawingId(data: DrawingsResponse, drawingId?: string): boolean {
  return Boolean(drawingId && data.some(drawing => drawing.drawingId === drawingId));
}

function parseDrawingDate(drawingDate?: string): number {
  const drawingMs = Date.parse(drawingDate ?? '');
  return Number.isFinite(drawingMs) ? drawingMs : Number.NaN;
}

/** An unparseable date stays in the list; only a draw still ahead of `now` is undrawn. */
function hasDrawingStarted(drawing: { drawingDate?: string }, now: number): boolean {
  const drawingMs = parseDrawingDate(drawing.drawingDate);
  if (!Number.isFinite(drawingMs)) return true;

  return drawingMs <= now;
}

function hasStartedDrawingId(data: DrawingsResponse, drawingId: string | undefined, now: number): boolean {
  if (!drawingId) return false;

  const drawing = data.find(item => item.drawingId === drawingId);
  return drawing != null && hasDrawingStarted(drawing, now);
}

function getCloseToDrawGap({ nextDrawingDate, nextClosingDate }: PickerDrawingOptions): number {
  if (nextDrawingDate == null || nextClosingDate == null) return 0;

  return nextDrawingDate - nextClosingDate;
}

function isInsideLeadTime(now: number, leadTime: number, closingDate: number, drawingDate: number): boolean {
  return now < drawingDate && now + leadTime >= closingDate;
}

function getSoonestUndrawnDrawing(data: DrawingsResponse, now: number): Drawing | undefined {
  return data.reduce<Drawing | undefined>((soonest, drawing) => {
    const drawingMs = parseDrawingDate(drawing.drawingDate);
    if (!Number.isFinite(drawingMs) || drawingMs <= now) return soonest;
    if (!soonest) return drawing;

    return drawingMs < parseDrawingDate(soonest.drawingDate) ? drawing : soonest;
  }, undefined);
}

/**
 * Close used for an undrawn row. The next draw keeps the closing date the API reported. Any earlier
 * row uses the same close-to-draw gap, so a clock set to the previous day still opens at that close.
 */
function getUndrawnClosingDate(drawing: Drawing, options: PickerDrawingOptions): number {
  const drawingMs = parseDrawingDate(drawing.drawingDate);
  if (drawing.drawingId === options.nextDrawingId && options.nextClosingDate != null) {
    return options.nextClosingDate;
  }

  return drawingMs - getCloseToDrawGap(options);
}

/** The soonest undrawn result that is already inside the countdown window, when it is not after next-draw. */
function getUndrawnLeadDrawing(data: DrawingsResponse, options: PickerDrawingOptions): Drawing | undefined {
  const { nextCloseLeadTime, nextDrawingDate, now = Date.now() } = options;
  if (!nextCloseLeadTime) return undefined;

  const soonest = getSoonestUndrawnDrawing(data, now);
  if (!soonest) return undefined;

  const drawingMs = parseDrawingDate(soonest.drawingDate);
  if (nextDrawingDate != null && drawingMs > nextDrawingDate) return undefined;
  if (!isInsideLeadTime(now, nextCloseLeadTime, getUndrawnClosingDate(soonest, options), drawingMs)) return undefined;

  return soonest;
}

function withholdUndrawnResult(drawing: Drawing): Drawing {
  const withheld = { ...drawing, numbers: [], odds: [] };
  delete withheld.extraNumbers;
  delete withheld.drawnNumbers;
  delete withheld.drawnExtraNumbers;

  return withheld;
}

function getDrawingIndex(drawingId: string): number | undefined {
  const match = /_(\d+)$/.exec(drawingId);
  if (!match) return undefined;

  return Number(match[1]);
}

function getPreviousDrawingId(drawingId: string): string | undefined {
  const match = /^(.*_)(\d+)$/.exec(drawingId);
  if (!match) return undefined;

  const previousIndex = Number(match[2]) - 1;
  if (previousIndex < 1) return undefined;

  return `${match[1]}${previousIndex}`;
}

function hasSkippedUpcomingDrawing(results: Array<{ drawingId: string }> | null | undefined, nextDrawingId?: string): boolean {
  if (!results?.length || !nextDrawingId) return false;

  const nextIndex = getDrawingIndex(nextDrawingId);
  if (nextIndex == null) return false;

  const latestIndex = results.reduce<number | undefined>((latest, drawing) => {
    const index = getDrawingIndex(drawing.drawingId);
    if (index == null) return latest;
    if (latest == null || index > latest) return index;
    return latest;
  }, undefined);

  if (latestIndex == null) return false;

  return nextIndex >= latestIndex + 2;
}

/**
 * A clock change between two drawings moves the interval between them by exactly an hour: the draw
 * keeps its local time, so the UTC gap is an hour short (or long) across it.
 */
const CLOCK_CHANGE_MS = 60 * 60 * 1000;

function getIntervals(times: number[], step: number): number[] {
  return times.slice(0, -step).map((time, index) => time - times[index + step]!);
}

function isRepeatingInterval(times: number[], step: number, periodMs: number): boolean {
  return getIntervals(times, step).every(interval => interval === periodMs || Math.abs(interval - periodMs) === CLOCK_CHANGE_MS);
}

/** The interval most drawings kept, so one that spans a clock change does not set the period. */
function getMostCommonInterval(times: number[], step: number): number {
  const counts = new Map<number, number>();
  getIntervals(times, step).forEach(interval => counts.set(interval, (counts.get(interval) ?? 0) + 1));

  return [...counts].reduce((best, entry) => (entry[1] > best[1] ? entry : best))[0];
}

function findLotteryCycle(times: number[]): { periodMs: number; step: number } | undefined {
  if (times.length < 2) return undefined;

  // Fetched results are assumed to include at least one full lottery schedule cycle.
  // Use the smallest interval from the newest draw (index 0) to the nth draw (index n-1)
  // that repeats at every index step of n-1, give or take a clock change. Fall back to the
  // interval between the first and last fetched result.
  const steps = Array.from({ length: times.length - 1 }, (_, index) => index + 1);
  const step = steps.find(candidateStep => {
    const periodMs = getMostCommonInterval(times, candidateStep);
    return periodMs > 0 && isRepeatingInterval(times, candidateStep, periodMs);
  });
  if (step == null) return undefined;

  return { periodMs: getMostCommonInterval(times, step), step };
}

function inferSkippedUpcomingDraw(data: Array<{ drawingDate?: string }>, options: PickerDrawingOptions) {
  if (options.nextDrawingDate == null || options.nextClosingDate == null) return undefined;

  const times = data
    .map(drawing => new Date(drawing.drawingDate ?? '').getTime())
    .filter(time => Number.isFinite(time))
    .sort((aa, bb) => bb - aa);
  const cycle = findLotteryCycle(times);
  if (!cycle) return undefined;

  const drawingDate = times[cycle.step - 1]! + cycle.periodMs;
  const closingDate = drawingDate - (options.nextDrawingDate - options.nextClosingDate);

  return { drawingDate, closingDate };
}

function getUpcomingPlaceholderDate(data: DrawingsResponse, options: PickerDrawingOptions): number | undefined {
  if (!hasSkippedUpcomingDrawing(data, options.nextDrawingId)) return options.nextDrawingDate;

  return inferSkippedUpcomingDraw(data, options)?.drawingDate ?? options.nextDrawingDate;
}

function shouldIncludeUpcomingDraw(data: DrawingsResponse, options: PickerDrawingOptions): boolean {
  const { nextCloseLeadTime, nextDrawingDate, nextClosingDate, nextDrawingId, lotteryName, now = Date.now() } = options;
  if (!nextCloseLeadTime || nextDrawingDate == null || nextClosingDate == null || !nextDrawingId || !lotteryName) return false;

  if (hasSkippedUpcomingDrawing(data, nextDrawingId)) {
    return !hasStartedDrawingId(data, getPreviousDrawingId(nextDrawingId), now);
  }

  if (hasStartedDrawingId(data, nextDrawingId, now)) return false;
  if (getUndrawnLeadDrawing(data, options)) return true;
  if (hasPublishedDrawingId(data, nextDrawingId)) return false;

  return now + nextCloseLeadTime >= nextClosingDate;
}

function createUpcomingPlaceholder(data: DrawingsResponse, options: PickerDrawingOptions): Drawing {
  const nextDrawingId = options.nextDrawingId!;
  const drawingDate = getUpcomingPlaceholderDate(data, options)!;
  // Same id `inferClosedUndrawn` uses once the inferred draw sits clear of next-draw: the one before it.
  const isClosedUndrawn = options.nextDrawingDate != null && drawingDate < options.nextDrawingDate - CLOCK_CHANGE_MS;

  return {
    drawingId: isClosedUndrawn ? (getPreviousDrawingId(nextDrawingId) ?? nextDrawingId) : nextDrawingId,
    lotteryId: options.lotteryName!,
    drawingDate: new Date(drawingDate).toISOString(),
    numbers: [],
    jackpots: [],
    odds: [],
  };
}

function limitDrawings(drawings: Drawing[], maxDrawings?: number): Drawing[] {
  if (maxDrawings == null) return drawings;
  return drawings.slice(0, maxDrawings);
}

function getPickerDrawings(data: DrawingsResponse, options: PickerDrawingOptions = {}): Drawing[] {
  const now = options.now ?? Date.now();
  const sorted = sortDrawingsDescending(data);
  if (!options.nextCloseLeadTime) return limitDrawings(sorted, options.maxDrawings);

  const started = sorted.filter(drawing => hasDrawingStarted(drawing, now));
  if (!shouldIncludeUpcomingDraw(data, options)) return limitDrawings(started, options.maxDrawings);

  const undrawnLead = hasSkippedUpcomingDrawing(data, options.nextDrawingId) ? undefined : getUndrawnLeadDrawing(data, options);
  const upcoming = undrawnLead ? withholdUndrawnResult(undrawnLead) : createUpcomingPlaceholder(data, options);

  return limitDrawings([upcoming, ...started], options.maxDrawings);
}

function getDefaultDrawing(pickerDrawings: Drawing[], options: PickerDrawingOptions = {}): Drawing | null {
  const [latestDrawing] = pickerDrawings;
  if (!latestDrawing) return null;
  if (!shouldIncludeUpcomingDraw(pickerDrawings, options)) return latestDrawing;
  return pickerDrawings.find(drawing => isUpcomingDrawing(drawing, options)) ?? latestDrawing;
}

export { CLOCK_CHANGE_MS, getDefaultDrawing, getPickerDrawings, getPreviousDrawingId, hasSkippedUpcomingDrawing, inferSkippedUpcomingDraw, shouldIncludeUpcomingDraw };
export type { PickerDrawingOptions };
